import { userInitials } from '@/lib/utils';
import { normalizeUrlOnBlur, normalizeUrlWhenPaste } from '@/utils/normalize';
import { Loader2 } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/Auth';
import CustomSelect from '../shared/CustomSelect/CustomSelect';
import {
  defaultRefundDeadlineDays,
  getRefundDeadlineDaysSelectOptions,
  isAllowedRefundDeadlineDays,
  normalizeRefundDeadlineDays,
} from '@/constants/refundDeadline';
import { Input } from '../shared/Input/Input';
import { Label } from '../shared/Label/Label';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Textarea } from '../ui/textarea';
import ProductUuidBadge from './product-uuid-badge';

export default function DetailsSection({
  canEditDetails,
  canUploadImage,
  hasImageChanged,
  setHasImageChanged,
  setValue,
  product,
  isUploadingImage,
  t,
  productNamePreview,
  register,
  errors,
  formValues,
  showUrlPages = false,
}) {
  const { i18n } = useTranslation();
  const { authData } = useAuth();
  const lang = (i18n.language || 'pt').split('-')[0];
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const handleUploadPhoto = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        setHasImageChanged(true);
        setValue('image', file);
        const previewURL = URL.createObjectURL(file);
        setImagePreview(previewURL);
      }
    },
    [setValue]
  );

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const refundDeadlineOptions = getRefundDeadlineDaysSelectOptions(
    t,
    formValues?.category_type,
  )
  const defaultRefundDays = defaultRefundDeadlineDays(formValues?.category_type)

  useEffect(() => {
    const current = Number(formValues?.refund_deadline_days)
    if (!isAllowedRefundDeadlineDays(current, formValues?.category_type)) {
      setValue('refund_deadline_days', defaultRefundDays, { shouldDirty: false })
    }
  }, [defaultRefundDays, formValues?.category_type, formValues?.refund_deadline_days, setValue])

  return (
    <>
      <article className='flex items-center mt-6 gap-4'>
        <Avatar className='h-14 w-14 bg-blackA1'>
          <AvatarImage
            src={
              hasImageChanged && imagePreview
                ? imagePreview
                : product?.images?.[0]?.url
            }
            className='h-full w-full'
            alt={productNamePreview}
            loading='lazy'
            data-cy='product-image-preview'
          />
          <AvatarFallback className='bg-elevated text-xl font-medium text-muted'>
            {userInitials(productNamePreview)}
          </AvatarFallback>
        </Avatar>

        {canUploadImage && (
          <div>
            <label
              htmlFor='upload-photo'
              data-cy='product-image-input'
              className={`bg-elevated px-4 py-2 rounded-lg text-sm text-colorText h-9 inline-flex items-center ${isUploadingImage
                ? 'opacity-50 cursor-not-allowed'
                : 'cursor-pointer'
                }`}
            >
              {isUploadingImage ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  {t('uploading')}
                </>
              ) : (
                t('change_photo')
              )}
            </label>
            <input
              id='upload-photo'
              type='file'
              accept='image/*'
              className='hidden'
              onChange={handleUploadPhoto}
              disabled={isUploadingImage}
            />
          </div>
        )}
      </article>
      <section className='mt-6 space-y-6'>
        <Input
          {...register('name')}
          placeholder={t('productNamePlaceholder')}
          label={t('productNameLabel')}
          helperText={errors?.name?.message}
          disabled={!canEditDetails}
          data-cy='product-name-input'
        />

        {authData?.public_api_enabled && (
          <ProductUuidBadge uuid={product?.id} />
        )}

        <CustomSelect
          placeholder={t('categoryPlaceholder')}
          options={[
            {
              value: product?.category.id,
              label:
                product?.category.name_translations?.[lang] ||
                product?.category.name,
            },
          ]}
          label={t('category')}
          value={product?.category.id}
          disabled
        />

        <div className='flex gap-2 flex-col'>
          <Label htmlFor='description'>{t('productDescriptionLabel')}</Label>
          <Textarea
            {...register('description')}
            placeholder={t('productDescriptionPlaceholder')}
            maxLength={500}
            className={`focus:outline-none ring-0 focus-visible:outline-none ${errors?.description ? 'border-error' : 'border-colorBorder'
              }`}
            disabled={!canEditDetails}
            data-cy='product-description-textarea'
          />
          <p className='text-muted text-xs -mt-1'>
            {formValues?.description?.length || 0}/500
          </p>
          {errors?.description?.message && (
            <p className='text-sm text-error'>{errors.description.message}</p>
          )}
        </div>

        <Input
          {...register('sales_page')}
          label={t('sell_page')}
          placeholder='https://'
          helperText={errors?.sales_page?.message}
          disabled={!canEditDetails}
          data-cy='product-sales-page-input'
          onFocus={(e) => {
            if (!e.target.value) {
              e.target.value = 'https://';
              setValue('sales_page', 'https://');
            }
          }}
          onPaste={(e) => {
            const finalValue = normalizeUrlWhenPaste(e);
            setValue('sales_page', finalValue);
          }}
          onBlur={(e) => {
            const normalized = normalizeUrlOnBlur(e.target.value);
            e.target.value = normalized;
            setValue('sales_page', normalized);
          }}
        />

        
        <CustomSelect
          label={t('refundDeadlineDaysLabel', 'Prazo de reembolso (dias)')}
          placeholder={t('refundDeadlineDaysPlaceholder', 'Selecione o prazo')}
          options={refundDeadlineOptions}
          value={String(
            normalizeRefundDeadlineDays(
              formValues?.refund_deadline_days,
              formValues?.category_type,
            ),
          )}
          onValueChange={(value) => {
            setValue(
              'refund_deadline_days',
              normalizeRefundDeadlineDays(value, formValues?.category_type),
              { shouldDirty: true, shouldValidate: true },
            )
          }}
          helperText={errors?.refund_deadline_days?.message}
          disabled={!canEditDetails}
          dataCy="refund-deadline-days"
          id="refundDeadlineDays"
        />
<Input
          {...register('thankyou_url')}
          label={`${t('common:thankyouUrlLabel')} (${t('common:optional')})`}
          placeholder={t('common:thankyouUrlPlaceholder')}
          helperText={errors?.thankyou_url?.message}
          subLabel={t('common:thankyouUrlDescription')}
          disabled={!canEditDetails}
          data-cy='product-thankyou-url-input'
          onFocus={(e) => {
            if (!e.target.value) {
              e.target.value = 'https://';
              setValue('thankyou_url', 'https://');
            }
          }}
          onPaste={(e) => {
            const finalValue = normalizeUrlWhenPaste(e);
            setValue('thankyou_url', finalValue);
          }}
        />

        {showUrlPages && (
          <>
            <Input
              {...register('support_url', {
                setValueAs: (v) => v?.trim().replace(/\s/g, ''),
              })}
              label={t('onboarding:user_support_page')}
              placeholder={t('onboarding:user_support_page_placeholder')}
              helperText={errors?.support_url?.message}
              disabled={!canEditDetails}
              data-cy='product-support-url-input'
              onFocus={(e) => {
                if (!e.target.value) {
                  e.target.value = 'https://';
                  setValue('support_url', 'https://');
                }
              }}
              onPaste={(e) => {
                const finalValue = normalizeUrlWhenPaste(e);
                setValue('support_url', finalValue);
              }}
              onBlur={(e) => {
                const normalized = normalizeUrlOnBlur(e.target.value);
                e.target.value = normalized;
                setValue('support_url', normalized);
              }}
            />

            <Input
              {...register('privacy_terms_url', {
                setValueAs: (v) => v?.trim().replace(/\s/g, ''),
              })}
              label={t('onboarding:user_privacy_terms_page')}
              placeholder={t('onboarding:user_privacy_terms_page_placeholder')}
              helperText={errors?.privacy_terms_url?.message}
              disabled={!canEditDetails}
              data-cy='product-privacy-terms-url-input'
              onFocus={(e) => {
                if (!e.target.value) {
                  e.target.value = 'https://';
                  setValue('privacy_terms_url', 'https://');
                }
              }}
              onPaste={(e) => {
                const finalValue = normalizeUrlWhenPaste(e);
                setValue('privacy_terms_url', finalValue);
              }}
              onBlur={(e) => {
                const normalized = normalizeUrlOnBlur(e.target.value);
                e.target.value = normalized;
                setValue('privacy_terms_url', normalized);
              }}
            />
          </>
        )}
      </section>
    </>
  );
}